// Daemon status — the 3-state connection mapping, the 5-state per-daemon health
// mapping the Home card shows, and the agent-roster guard. Kept as PURE
// functions (no $state, no live-store imports) so all three are unit-testable
// under plain-node vitest; the live wrappers that read the reactive stores live
// in app.svelte.ts (which has import side-effects).

export type DaemonConnStatus = "connecting" | "connected" | "offline";

// Collapse the relay socket status + daemon health into the 3 states the UI
// shows. Inputs are plain values (snapshotted from connectionState /
// daemonHealthState / daemonStatusState by the caller) so this stays pure.
//
// Mapping:
//  - connected  → a daemon is provably reachable: at least one daemon is online
//    (daemon_status broadcast), or health reports fully connected. Checked FIRST
//    and authoritative — the /api/health poll does NOT run in every deployment
//    mode (e.g. the cloud desktop app never hits a local daemon's /api/health),
//    so we must NOT gate "connected" behind health having been polled. onlineCount
//    is the signal that survives cloud/relay mode.
//  - connecting → no daemon online yet AND we're still in the relay
//    handshake/cold-start window (relay `reconnecting`, or never connected this
//    session). This is transient — it resolves the moment a daemon reports in.
//  - offline    → relay is up but no daemon is answering.
//
// NOTE: `healthLastChecked` is intentionally no longer part of the logic — gating
// on it caused a permanent "connecting" whenever /api/health wasn't polled. Kept
// in the input shape for the live wrapper's call site (harmless, unused here).
export function mapDaemonConnStatus(input: {
  relayStatus: "disconnected" | "connected" | "reconnecting";
  hasConnectedOnce: boolean;
  healthLastChecked: number;
  healthOverall: "connected" | "partial" | "disconnected";
  onlineCount: number;
}): DaemonConnStatus {
  if (input.onlineCount > 0 || input.healthOverall === "connected") {
    return "connected";
  }
  if (input.relayStatus === "reconnecting" || !input.hasConnectedOnce) {
    return "connecting";
  }
  return "offline";
}

// ─── My daemon's 4-state health (the Home card, Figma 1644:2701 / 1894:3069) ──
// Mirrors the SERVER's DAEMON_ONLINE_WINDOW_MS (daemon-liveness.ts). Same number
// on both sides on purpose: the relay uses it to decide a daemon is alive, so if
// the page used its own threshold the two would disagree about the same daemon.
// (The daemon heartbeats every 30s, so this is two missed beats.)
export const HEARTBEAT_STALE_MS = 60_000;

// Give a temporarily-lost daemon a reconnect grace window before we call it dead.
// The daemon heartbeats every ~30s, so ~90s is roughly 3 missed heartbeats: two
// missed beats already makes it stale (HEARTBEAT_STALE_MS above), and one more
// missed retry is where we stop calling it "reconnecting" and admit it's gone.
// While offline/stale but still inside this window → "connecting"; past it →
// "unreachable".
export const UNREACHABLE_AFTER_MS = 90_000;

// The minimum visible "Connecting.." beat right after a daemon connects, before
// we call it healthy — a small, honest floor. The browser can only observe the
// daemon AFTER it has connected (the first `daemon_status:"online"` push), so it
// cannot show the real pre-connect boot; without this floor a dead daemon that
// boots + connects snaps straight from "Unreachable" → "Healthy" with no
// connecting beat. Stamped on the live offline→online transition (connectedAt);
// a fuller "daemon emits a real starting signal" is a separate future change.
export const SETTLE_MS = 2_000;

export type DaemonHealth = "not-set-up" | "connecting" | "healthy" | "unreachable";

// Derived from the signals we actually have — no invented telemetry:
//   not-set-up   no daemon owned by this user at all
//   connecting   we don't yet know enough to call it healthy AND haven't given
//                up on it: a restart WE asked for is in flight, OR the desktop
//                shell reports the process is still starting, OR the daemon is
//                connected+fresh but its agent roster hasn't answered yet (cold
//                start), OR the daemon is offline/stale but still inside the
//                reconnect grace window (< UNREACHABLE_AFTER_MS since lastSeenAt).
//                This MERGES the old "starting" and "not-responding" states —
//                both are "we're still trying", and the user shouldn't have to
//                tell a booting daemon apart from a briefly-dropped one.
//   healthy      online, heartbeat fresh, AND its roster has actually landed
//   unreachable  offline/stale and the reconnect grace window has elapsed — it
//                has missed ~3 heartbeats and isn't coming back on its own
//
// `rosterPending` is the fix for the "Healthy · 0 agents · 0 sessions" lie right
// after launch (reported 2026-08-19). A fresh heartbeat proves the PROCESS is
// alive; it does not prove the daemon-forwarded `list_agents` fan-out has come
// back. On a cold start it usually hasn't — the DM roster is empty for another
// ~10-30s while retryAgentsOnEmpty backs off (see app.svelte.ts) — and calling
// that "Healthy" contradicts the empty DM list the user is looking at. It stays
// "Connecting.." ("Connected 29s ago · loading agents…") until the roster
// answers. Deliberately NOT a plain `agents === 0` check: a daemon that
// genuinely hosts no agents is healthy, and would otherwise be pinned at
// "Connecting.." forever.
//
// The "have we given up?" decision is a PURE function of elapsed time since
// `lastSeenAt` (≈ missed-heartbeat count), so no external attempt counter or
// live state is needed here.
export function mapDaemonHealth(input: {
  // A restart WE asked for (the Home card's Reconnect button).
  restarting: boolean;
  // The desktop shell's own process phase — it spawned the daemon, so it knows.
  // null in a plain browser (no bridge).
  localPhase: "starting" | "running" | "stopped" | "errored" | null;
  daemon: { online: boolean; lastSeenAt: number | null; agents: number } | null;
  // The agent roster for this daemon hasn't been answered yet (cold-start
  // retry-on-empty still in flight).
  rosterPending: boolean;
  // ms timestamp of the last LIVE offline→online transition for THIS daemon, or
  // null (never transitioned this session / has been online since load / offline).
  // Within SETTLE_MS of it we show a "connecting" beat before "healthy".
  connectedAt: number | null;
  now: number;
}): DaemonHealth {
  if (!input.daemon) return "not-set-up";
  // A start we KNOW is in flight outranks the liveness checks: the row may still
  // read offline because the process hasn't reported in yet — that's precisely
  // "connecting", not "unreachable".
  if (input.restarting || input.localPhase === "starting") return "connecting";

  const offlineOrStale =
    !input.daemon.online ||
    (input.daemon.lastSeenAt != null && input.now - input.daemon.lastSeenAt > HEARTBEAT_STALE_MS);

  if (offlineOrStale) {
    // Still within the reconnect grace window (< ~3 missed heartbeats since we
    // last heard from it) → keep trying, show "connecting". Once elapsed → dead.
    if (
      input.daemon.lastSeenAt != null &&
      input.now - input.daemon.lastSeenAt < UNREACHABLE_AFTER_MS
    ) {
      return "connecting";
    }
    return "unreachable";
  }

  // Online + fresh, but the roster is still pending on a cold start → we can't
  // honestly show what it's hosting yet.
  if (input.rosterPending && input.daemon.agents === 0) return "connecting";

  // Terminal path (online + fresh + roster settled). One last honest floor: if
  // the daemon only JUST transitioned offline→online, hold a brief "connecting"
  // beat so a reconnect/boot doesn't snap straight to "healthy" with no visible
  // connecting state. `connectedAt` is null whenever offline, so it can never
  // mask the unreachable/stale checks above — and this sits BELOW them anyway.
  if (input.connectedAt != null && input.now - input.connectedAt < SETTLE_MS) {
    return "connecting";
  }
  return "healthy";
}

// ─── Reconcile a grace-based daemon-list load against live realtime status ────
//
// The SAME class of fix as nextAgentSnapshot above: "don't let a stale refetch
// clobber authoritative live state." Here the live state is a realtime
// `daemon_status` push (relay → daemonStatusState.set), and the stale refetch is
// the 30s `list_daemons` liveness poll (refreshDaemonLiveness → load).
//
// The bug this closes: the relay computes each list row's `status` through a 60s
// `isDaemonOnline` grace window (server/cyborg/daemon-liveness.ts), so a daemon
// that disconnected < 60s ago is STILL reported "online" in the list. A blind
// full-replace load() therefore overwrites the authoritative `offline` the relay
// just PUSHED with a grace-"online", and the Home card lights up a false
// "Healthy" for a daemon that is actually dead (it oscillates
// Connecting.. → false-Healthy → Connecting.. → Unreachable).
//
// Mirrors DaemonState.isOnline (plugins/agents/state.svelte.ts): a live override
// wins over the list-derived value wherever one exists, and is cleared ONLY by a
// later contradicting live push — NOT time-bounded. That is the exact policy
// DaemonState uses (its `liveStatus` map is cleared only by another setStatus or
// clear(), never on a timer), kept identical so the Home card and the sidebar
// dots can't disagree about the same daemon.
//
// - `live[id]` present AND `id` is in the loaded list → the override wins (a
//   realtime push is "true right now", the list row is only "true when the
//   relay's grace window last evaluated").
// - `live[id]` absent   → take the loaded value (no realtime signal this session,
//   or the daemon isn't one we've had a push for — the list is all we know).
// - `id` NOT in the loaded list → the daemon is gone; its override is dropped so a
//   stale entry can't linger. This is what lets `load([])` at logout fully reset
//   both maps (the class has no `clear()` caller), and prunes a removed daemon on
//   a normal refresh — a small, deliberate refinement over DaemonState, whose
//   `liveStatus` is instead reset by an explicit `clear()` at logout.
//
// A genuine reconnect is NORMALLY handled by the caller writing `set(id, "online")`
// on the `daemon_status:"online"` push, which replaces the "offline" override; the
// very next load then simply agrees.
//
// But that push is a ONE-SHOT EDGE. It is emitted only from onDaemonConnect /
// onDaemonDisconnect (relay-standalone.ts) — there is no daemon-presence snapshot
// on subscribe and no periodic re-emit. A client whose own socket is down across
// that instant never receives it, and the "offline" override then outlived every
// refetch FOREVER: load() re-fetched the correct "online" from PG and this
// function threw it away. Two real ways to miss it, both observed on 2026-08-24:
// a laptop waking from hibernation while the daemon self-reconnects, and the
// sidebar's own ↻ Reconnect button, which calls forceReconnect() right as the
// daemon it just restarted re-registers. The only cure was quitting the app.
//
// So an offline override is now ALSO dropped by strictly newer SERVER evidence:
// once `lastSeenAt` has advanced past what it was when the override was recorded,
// the relay has heard from that daemon SINCE it told us the daemon was gone —
// proof of life that no missed edge can take back. Both sides of the comparison
// come from PG, so there is no client-vs-relay clock skew in it.
//
// This does NOT weaken the grace-window fix above: a dead daemon stops
// heartbeating, so its `lastSeenAt` is frozen at the moment it died. The
// comparison fails and the override correctly stands for the whole 60s the list
// keeps claiming "online".
export function reconcileDaemonStatuses(
  loaded: Record<string, "online" | "offline">,
  live: Record<string, "online" | "offline">,
  // `lastSeenAt` per daemon as of THIS load, and what it was when each override
  // was recorded. Both default to {} so a caller that supplies neither keeps the
  // old behaviour exactly — with no timestamps, nothing can refute an override.
  lastSeen: Record<string, number> = {},
  overrideBaseline: Record<string, number> = {},
): {
  statuses: Record<string, "online" | "offline">;
  live: Record<string, "online" | "offline">;
} {
  const statuses: Record<string, "online" | "offline"> = { ...loaded };
  const prunedLive: Record<string, "online" | "offline"> = {};
  for (const [id, status] of Object.entries(live)) {
    // Keep an override only for a daemon still present in the loaded list; a
    // daemon absent from the list is gone, so its override is dropped.
    if (!(id in loaded)) continue;
    // Drop an "offline" override the server has since contradicted. Requires a
    // recorded baseline: without one we cannot tell a post-death `lastSeenAt`
    // (still inside the grace window) from a genuine new heartbeat, and guessing
    // would resurrect the false-"Healthy" bug this override exists to prevent.
    const baseline = overrideBaseline[id];
    if (status === "offline" && baseline !== undefined && (lastSeen[id] ?? 0) > baseline) {
      continue;
    }
    statuses[id] = status;
    prunedLive[id] = status;
  }
  return { statuses, live: prunedLive };
}

// Guard the daemon-proxied `listAgents()` result before it replaces the roster.
// list_agents is forwarded to the daemon, so it returns [] while the daemon is
// offline/connecting — applying that empty snapshot would wipe the DM agent rows
// (they should instead persist, rendered greyed via ChannelSidebar's
// agentDotClass). Keep the last-known list UNLESS the empty result came back
// while a daemon is actually reachable, which is a genuine "no agents" clear.
// PARTIAL ANSWERS (live regression, 2026-08-20). list_agents fans out to EVERY
// daemon in the workspace and the relay finalizes on a fixed 4s window, answering
// with whatever came back. A daemon that is merely SLOW — the owner's sat blocked
// 7.2s inside a synchronous `VACUUM` on a 450 MB cyborg7.db — misses that window,
// so the relay returns a SHORT but NON-EMPTY roster and the empty-only guard above
// waved it straight through: every agent homed on the slow daemon vanished from the
// DM sidebar, and was then persisted into the cold-start cache. Third instance of
// one class in 24h (#2371 agent timeline, #2376 channel history): empty was
// handled, short was not.
//
// The fix is a UNION KEYED BY DAEMON, NOT "refuse partial answers". Refusing would
// let a single chronically hung-but-connected daemon freeze the entire roster
// forever — silent and permanent, strictly worse than a self-healing truncation.
// Instead: rows are REPLACED for the daemons that answered and CARRIED OVER for the
// ones that did not, so a wedged daemon can only ever stale its OWN rows (rendered
// greyed by ChannelSidebar's agentDotClass) while every other daemon's rows —
// additions and removals alike — keep updating normally.
//
// `silentDaemonIds` is the relay's `pendingDaemons`: daemons the fan-out REACHED
// that stayed silent (relay-standalone.ts finalizeAgentList). It is deliberately
// the reached-but-silent set and not "every daemon absent from the answer" — a
// daemon that fully DISCONNECTS is never reached, so it is never carried, and its
// rows drop on the next list instead of being pinned by this guard for good.
//
// Why the relay has to send it instead of us deriving it from `next`: a daemon that
// answers with ZERO agents contributes no rows, so a set derived from the answer
// could not tell it apart from a silent one — we would carry its stale rows forever
// and a genuinely removed agent would never disappear.
//
// Keying on `daemonId` is sound because every writer of that field is the relay's
// own authenticated WS connection identity (`fromDaemonId` → agent_sessions.daemon_id
// → stampSessionDaemonOwners), never daemon-supplied payload — the same id space
// `pendingDaemons` is drawn from. It IS nullable, though: a still-null daemonId is
// the relay's final word on "nobody hosts this session". Null-homed rows are
// therefore never carried — no daemon can answer for them, so carrying them would
// resurrect rows the relay deliberately dropped.
export function nextAgentSnapshot<T extends { agentId?: string; daemonId?: string | null }>(
  current: T[],
  next: T[],
  daemonConnected: boolean,
  silentDaemonIds?: readonly string[],
): T[] {
  if (next.length === 0 && !daemonConnected) return current;
  if (!silentDaemonIds || silentDaemonIds.length === 0) return next;
  const silent = new Set(silentDaemonIds);
  const carried = current.filter((a) => !!a.daemonId && silent.has(a.daemonId));
  if (carried.length === 0) return next;
  // A row the answer DID carry is authoritative — the carry-over only fills gaps,
  // so a silent daemon's row still loses to a fresher one from the PG offline union.
  const answered = new Set(next.map((a) => a.agentId));
  return [...next, ...carried.filter((a) => !answered.has(a.agentId))];
}
